// useGenesisRuns
//
// Fetches the live Genesis run set from the T1 org-backend
// (GET /api/genesis/runs, bare array, same shape as the dummy export).
// Falls back to the in-repo dummy data so the page never blanks if the
// network is down or the response is empty.
//
// Base URL resolution and (currently unauthenticated) fetch pattern
// mirror Enterprise.js / Enterprises.js for /api/orgs; when those gain
// auth, this hook picks up the same change naturally.
//
// Returns:
//   { runs, source, loading }
//     runs   : array of run objects (live or fallback). null while loading.
//     source : 'live' | 'fallback-dummy' | 'loading'
//     loading: runs === null

import { useEffect, useState } from 'react';
import axios from 'axios';
import { IN_PROGRESS, PAIRS } from '../pages/enterprise-ai/genesisData';

const API_BASE = process.env.REACT_APP_ORG_BACKEND_URL || 'https://api.business.joinunited.ai';
const FALLBACK = [IN_PROGRESS, ...PAIRS];
const IS_DEV = process.env.NODE_ENV !== 'production';

export const useGenesisRuns = () => {
  const [runs, setRuns] = useState(null);
  const [source, setSource] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/genesis/runs`);
        // Normalize the top-level run id. The org-backend uses MongoDB's
        // _id convention (same as /api/orgs); page components key on r.id
        // and compare selectedRun === r.id, so copy _id (or runId, if the
        // backend ever uses that) into id when id is missing.
        const data = Array.isArray(res.data)
          ? res.data.map((r) => ({ ...r, id: (r && (r.id || r._id || r.runId)) }))
          : null;
        if (cancelled) return;
        if (data && data.length > 0) {
          if (IS_DEV) console.log('[GENESIS] data source: live');
          setRuns(data);
          setSource('live');
        } else {
          if (IS_DEV) console.log('[GENESIS] data source: fallback-dummy (empty response)');
          setRuns(FALLBACK);
          setSource('fallback-dummy');
        }
      } catch (err) {
        if (cancelled) return;
        if (IS_DEV) console.log('[GENESIS] data source: fallback-dummy (fetch failed)', err && err.message);
        setRuns(FALLBACK);
        setSource('fallback-dummy');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { runs, source, loading: runs === null };
};

export default useGenesisRuns;
